import React, { useEffect, useState } from 'react';
import { FaArrowUp, FaTimes } from 'react-icons/fa';
import { Button } from '../ui';
import { hasUpdater, onUpdateDownloaded, installUpdate } from '../../lib/desktopUpdater';

/**
 * Prompts the user to restart once the desktop app has downloaded a new
 * version. Dismissing it only hides the prompt — the update still installs the
 * next time the app quits. With unsaved changes on the page, Restart now asks
 * Leave / Stay first; on Stay the button is offered again.
 *
 * Renders nothing in a browser; the desktop shell is the only source of the
 * event that reveals it.
 */
const UpdateBanner = () => {
  const [version, setVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (!hasUpdater()) return undefined;
    return onUpdateDownloaded((v) => {
      setVersion(v);
      setDismissed(false);
    });
  }, []);

  if (!hasUpdater() || version === null || dismissed) return null;

  const handleUpdate = async () => {
    setInstalling(true);
    if ((await installUpdate()) === false) setInstalling(false);
  };

  return (
    <div
      role="status"
      className="fixed bottom-4 right-4 z-50 w-80 rounded-xl border border-lm-border bg-lm-surface-1 p-4 shadow-lg dark:border-dm-border dark:bg-dm-surface-1"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-primary-50 p-2 dark:bg-primary-900/40">
          <FaArrowUp className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-lm-text-primary dark:text-dm-text-primary">
            Update ready
          </p>
          <p className="mt-0.5 text-xs text-lm-text-muted dark:text-dm-text-muted">
            {version
              ? `Version ${version} is downloaded. Restart to use it.`
              : 'A new version is downloaded. Restart to use it.'}
          </p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={handleUpdate} disabled={installing}>
              {installing ? 'Restarting…' : 'Restart now'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDismissed(true)} disabled={installing}>
              Later
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="text-lm-text-muted hover:text-lm-text-primary dark:text-dm-text-muted dark:hover:text-dm-text-primary"
        >
          <FaTimes className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default UpdateBanner;
